import { useState } from 'react';
import type { TrackingEvent } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { Badge } from '../../components/ui/Badge';
import { trackingStatusPt } from '../../utils/enums-ptbr';

interface TrackingTimelineProps {
  events: TrackingEvent[];
}

/**
 * Rastreio timeline — shared by `/labels/:id` (`LabelDetailPage.tsx`) and
 * `/tracking` (`TrackingPage.tsx`), same component either way (TTP-244
 * "Arquivos no escopo").
 *
 * `event.raw` may carry recipient PII (e.g. the delivery address baked into
 * a provider's native payload) — hidden by default, per event, behind an
 * explicit "ver payload bruto" toggle (TTP-244 brief's security note: "só
 * sob toggle explícito, nunca em URL/query, nunca logado no console").
 */
export function TrackingTimeline({ events }: TrackingTimelineProps) {
  const [expandedRaw, setExpandedRaw] = useState<Set<number>>(new Set());

  function toggleRaw(index: number) {
    setExpandedRaw((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhum evento de rastreio ainda.</p>;
  }

  // `events[]` arrives in chronological order from the API — most recent
  // first is the more useful reading order for a timeline.
  const displayOrder = events.map((_, index) => index).reverse();

  return (
    <ol className="flex flex-col gap-4">
      {displayOrder.map((eventIndex) => {
        const event = events[eventIndex]!;
        const isExpanded = expandedRaw.has(eventIndex);
        const badgeVariant = event.status === 'delivered' ? 'success' : event.status === 'exception' ? 'error' : 'neutral';

        return (
          <li key={eventIndex} className="flex flex-col gap-2 border-l-2 border-border pl-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={badgeVariant}>{trackingStatusPt(event.status)}</Badge>
              <time dateTime={event.occurred_at} className="font-mono text-xs text-muted-foreground">
                {new Date(event.occurred_at).toLocaleString('pt-BR')}
              </time>
            </div>
            <p className="text-sm text-foreground">{event.description}</p>
            {event.location ? <p className="text-xs text-muted-foreground">{event.location}</p> : null}
            <div>
              <Button type="button" variant="ghost" size="sm" onClick={() => toggleRaw(eventIndex)}>
                {isExpanded ? 'Ocultar payload bruto' : 'Ver payload bruto'}
              </Button>
              {isExpanded ? (
                <pre className="mt-2 overflow-x-auto rounded-none border border-border bg-muted/30 p-3 font-mono text-xs text-foreground">
                  {JSON.stringify(event.raw, null, 2)}
                </pre>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
